import type { Language, QuoteStatus } from "@contracts/index";

const LOCALE: Record<Language, string> = { es: "es-MX", en: "en-US" };

export function formatMoney(cents: number, currency: string, language: Language = "es"): string {
  try {
    return new Intl.NumberFormat(LOCALE[language], { style: "currency", currency }).format(cents / 100);
  } catch {
    // Unknown currency code while typing: show the number with the raw code.
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

/** "1,234.50" or "1234.5" → 123450. Returns null when the text is not a valid amount. */
export function parseMoneyToCents(text: string): number | null {
  const normalized = text.replace(/[,\s]/g, "");
  if (!/^\d+(\.\d{0,2})?$/.test(normalized)) return null;
  return Math.round(Number(normalized) * 100);
}

export function centsToInput(cents: number): string {
  return (cents / 100).toFixed(2);
}

export function formatDate(iso: string, language: Language = "es"): string {
  const date = iso.length === 10 ? new Date(`${iso}T12:00:00Z`) : new Date(iso);
  return new Intl.DateTimeFormat(LOCALE[language], { dateStyle: "medium" }).format(date);
}

export function formatDateTime(iso: string): string {
  return new Intl.DateTimeFormat("es-MX", { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
}

export const STATUS_LABEL: Record<QuoteStatus, string> = {
  draft: "Borrador",
  sent: "Enviada",
  viewed: "Vista",
  accepted: "Aceptada",
  cancelled: "Cancelada",
};

export function statusLabel(status: QuoteStatus, expired: boolean): string {
  return expired ? "Vencida" : STATUS_LABEL[status];
}

export const EVENT_LABEL: Record<string, string> = {
  created: "Creada",
  updated: "Editada",
  duplicated: "Duplicada de otra cotización",
  sent: "Enviada",
  access_requested: "Acceso solicitado",
  opened: "Abierta por el cliente",
  downloaded: "PDF descargado",
  accepted: "Aceptada",
  draft_order_created: "Pedido creado en Shopify",
  cancelled: "Cancelada",
};

/** Common currencies offered in the editor; any ISO code can still be typed. */
export const COMMON_CURRENCIES = ["MXN", "USD", "EUR", "GTQ", "COP", "PEN", "CLP", "CRC", "DOP"];

/** Countries offered in the editor; any ISO 3166-1 alpha-2 code can still be typed. */
export const COMMON_COUNTRIES: Array<[string, string]> = [
  ["MX", "México"],
  ["US", "Estados Unidos"],
  ["ES", "España"],
  ["GT", "Guatemala"],
  ["SV", "El Salvador"],
  ["CO", "Colombia"],
  ["PE", "Perú"],
  ["CL", "Chile"],
  ["CR", "Costa Rica"],
  ["DO", "República Dominicana"],
  ["AR", "Argentina"],
  ["EC", "Ecuador"],
  ["HN", "Honduras"],
  ["PA", "Panamá"],
];
